import { PermissionGuard } from '@/components/PermissionGuard';
import { SendEsimInstructionsDialog } from '@/components/SendEsimInstructionsDialog';
import { fadeUp } from '@/lib/animations';
import { formatDate } from '@/lib/format';
import { simMapping } from '@/lib/simMapping';
import type { MobileLine } from '@octave/contracts';
import { motion } from 'framer-motion';
import {
  Calendar,
  CreditCard,
  Eye,
  EyeOff,
  Hash,
  Mail,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { DetailRow } from './DetailRow';
import { isLineActionsDisabled } from './helpers';
import { SwapSimCardDialog } from './SwapSimCardDialog';

interface LineSimTabProps {
  lineId: string;
  lineDetails: MobileLine;
}

/** SIM card tab: codes, eSIM activation block, swap + send-by-email dialogs. */
export function LineSimTab({ lineId, lineDetails }: LineSimTabProps) {
  const [showPuk, setShowPuk] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showSendEsimDialog, setShowSendEsimDialog] = useState(false);
  const [swapSimCardDialog, setSwapSimCardDialog] = useState(false);

  return (
    <>
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="max-w-3xl rounded-2xl bg-white p-6 shadow-sm"
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Carte SIM</h2>
            <p className="mt-0.5 text-sm text-slate-400">
              Informations et codes de sécurité
            </p>
          </div>
          {lineDetails.simCard && (
            <PermissionGuard permission="write">
              <button
                onClick={() => setSwapSimCardDialog(true)}
                disabled={isLineActionsDisabled(lineDetails.status)}
                className="rounded-xl bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50"
              >
                Changer de SIM
              </button>
            </PermissionGuard>
          )}
        </div>

        {!lineDetails.simCard ? (
          <div className="flex flex-col items-center py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50">
              <CreditCard className="h-7 w-7 text-slate-300" />
            </div>
            <p className="mt-4 text-sm font-medium text-slate-400">
              Aucune carte SIM liée à cette ligne
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <DetailRow
                icon={Hash}
                label="N° de série"
                value={lineDetails.simCard.serialNumber || '-'}
                mono
              />
              <DetailRow
                icon={CreditCard}
                label="Type"
                value={
                  simMapping[lineDetails.simCard.type ?? '-'] ||
                  lineDetails.simCard.type
                }
              />
              <DetailRow
                icon={Calendar}
                label="Activation"
                value={formatDate(lineDetails.activationDate ?? undefined)}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-xs text-slate-400">Code PIN</p>
                  <p className="mt-0.5 text-sm font-mono font-medium text-slate-900">
                    {showPin ? lineDetails.simCard.pinCode : '••••'}
                  </p>
                </div>
                <button
                  onClick={() => setShowPin(!showPin)}
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white hover:text-slate-600"
                >
                  {showPin ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-xs text-slate-400">Code PUK</p>
                  <p className="mt-0.5 text-sm font-mono font-medium text-slate-900">
                    {showPuk ? lineDetails.simCard.pukCode : '••••••••'}
                  </p>
                </div>
                <button
                  onClick={() => setShowPuk(!showPuk)}
                  className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white hover:text-slate-600"
                >
                  {showPuk ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* eSIM section */}
            {lineDetails.simCard.type === 'esim' &&
              lineDetails.simCard.activationCode &&
              (() => {
                const fullCode = lineDetails.simCard.activationCode;
                const manualCode = fullCode.includes('$')
                  ? fullCode.split('$').pop() || fullCode
                  : fullCode;

                return (
                  <div className="md:col-span-2 rounded-xl border border-slate-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-slate-700">
                        Code d'activation eSIM
                      </h3>
                      <button
                        onClick={() => setShowSendEsimDialog(true)}
                        className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-100"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Envoyer par email
                      </button>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-sm font-mono text-center text-slate-700 select-all tracking-wider">
                        {manualCode}
                      </p>
                    </div>

                    <div className="mt-6 flex flex-col gap-6 md:flex-row">
                      <div className="flex-shrink-0 flex justify-center">
                        <div className="rounded-xl border border-slate-100 bg-white p-3">
                          <QRCodeSVG
                            value={fullCode}
                            size={180}
                            level="M"
                            includeMargin={true}
                          />
                        </div>
                      </div>

                      <div className="flex-1 space-y-3">
                        <h4 className="text-sm font-semibold text-slate-700">
                          Instructions
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <p className="font-medium text-slate-700 mb-1">
                              iPhone :
                            </p>
                            <p className="text-slate-400">
                              Ouvrez "Réglages" → "Données cellulaires" →
                              "Ajouter une carte eSIM" → Scanner le QR Code
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-700 mb-1">
                              Android :
                            </p>
                            <p className="text-slate-400">
                              Ouvrez "Paramètres" → "Réseaux et Internet" OU
                              "Connexions" → "Carte SIM" ou "SIM" OU
                              "Gestionnaire de carte SIM" → "Ajouter une eSIM"
                              → Scanner un QR code
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
          </div>
        )}
      </motion.div>

      <SendEsimInstructionsDialog
        isOpen={showSendEsimDialog}
        onClose={() => setShowSendEsimDialog(false)}
        lineId={lineId}
        defaultEmail={lineDetails.identity?.email ?? undefined}
      />

      {swapSimCardDialog && (
        <SwapSimCardDialog
          isOpen={swapSimCardDialog}
          onClose={() => setSwapSimCardDialog(false)}
          lineId={lineId}
          operatorName={lineDetails.phonePlan.operator.name}
        />
      )}
    </>
  );
}
